import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Collapse, FormControlLabel, IconButton,
  MenuItem, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getOperationResults, listInstanceEvents, listOperations } from '../../../services/stacksetsApi';
import { formatStatusLabel } from '../../../utils/statusLabel';
import {
  buildAttempts, isRoleTrustReason, parseFailureReason, withCleanup,
} from '../../../utils/stackTimeline';
import StackTimeline from './StackTimeline';
import {
  CK, FONT_BODY, FONT_MONO, statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx,
  bodyCellSx, insetPanelSx, sectionHeadingSx, emptyTextSx,
} from '../portalStyles';

// Events poll fast while the instance's operation runs, slowly otherwise.
const EVENTS_POLL_RUNNING_MS = 5000;
const EVENTS_POLL_IDLE_MS = 30000;
const RUNNING_PHASES = ['pending', 'creating', 'updating', 'deleting'];
// How many recent operations to search for this cell's result reason.
const OP_RESULT_LOOKBACK = 5;

const attemptResultColor = (status) => ({
  success: 'success', failed: 'error', rollback: 'warning', in_progress: 'info',
}[status] || 'default');

// isCleanupAttempt reports whether attempts[i] is the automatic DELETE of the
// stack a failed CREATE left behind (buildAttempts sets `cleanup` from the
// delete's own "failed to create" reason, or a failed CREATE before it).
export const isCleanupAttempt = (attempts, i) => i > 0 && !!attempts[i] && !!attempts[i].cleanup;

// A cell AWS never attempted: the operation's failure tolerance was exceeded
// first. Its phase stays "failed" (Retry covers it); only the label differs.
export const isCancelledCell = (instance) => instance.phase === 'failed' && instance.detailedStatus === 'CANCELLED';

// ReasonText shows a StackSets failure reason as a readable one-liner
// ("EC2Instance (AWS::EC2::Instance): The key pair 'x' does not exist") with the
// raw AWS text (request id, token, handler code) behind a Details toggle.
export function ReasonText({ reason, sx }) {
  const [showRaw, setShowRaw] = useState(false);
  const { summary, details } = parseFailureReason(reason);
  if (!summary) return null;
  return (
    <Box component="span" sx={{ display: 'block', ...sx }}>
      {summary}
      {details && (
        <>
          {' '}
          <Box
            component="button"
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowRaw((v) => !v); }}
            sx={{
              border: 'none', background: 'none', p: 0, cursor: 'pointer', color: CK.blue,
              fontFamily: FONT_BODY, fontSize: '0.75rem', textDecoration: 'underline',
            }}
          >
            {showRaw ? 'Hide details' : 'Details'}
          </Box>
          {showRaw && (
            <Box component="span" sx={{ display: 'block', mt: 0.5, fontFamily: FONT_MONO, fontSize: '0.7rem', color: CK.muted, wordBreak: 'break-word' }}>
              {details}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// defaultAttemptIndex picks the attempt the panel opens on: the latest one,
// unless it is a finished auto-cleanup DELETE of a failed CREATE, in which case
// the failed CREATE (the one that says why it failed). A running cleanup stays
// selected so live progress is visible. -1 when there are no attempts.
export function defaultAttemptIndex(attempts) {
  let i = attempts.length - 1;
  if (i > 0 && attempts[i].status !== 'in_progress' && isCleanupAttempt(attempts, i)) i -= 1;
  return i;
}

const smallLabelSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };

const phaseColor = (phase) => ({
  current: 'success',
  outdated: 'warning',
  failed: 'error',
  deleted: 'default',
  skipped: 'default',
  pending: 'info',
  creating: 'info',
  updating: 'info',
  deleting: 'info',
  missing: 'error',
  role_missing: 'error',
}[phase] || 'default');

// CloudFormation resource-status → chip color (mirrors AWS console colouring).
const eventColor = (status) => {
  if (/FAILED|ROLLBACK/.test(status)) return 'error';
  if (/COMPLETE/.test(status)) return 'success';
  if (/IN_PROGRESS/.test(status)) return 'info';
  return 'default';
};

// InstanceEventsRow renders one StackSet instance row with an expandable panel
// showing the member-account CloudFormation stack's resource-level events (the
// AWS "Events" tab), as a table or as a timeline, one attempt at a time. The
// panel has a manual refresh button and an auto-refresh toggle that is ON by
// default: while the row is expanded the events re-poll every 5s while the
// instance's operation runs and every 30s otherwise. Once the operation is over
// the read uses source=auto, so a deleted stack still shows the events that
// were snapshotted when its operation finished. With no events at all, the
// instance's reason and the operation-result reason explain why (issue 3).
// extraCells (optional) renders after the Reason cell; colSpan is the parent
// table's column count so the expanded panel spans it.
export default function InstanceEventsRow({ instance, stackSetId, index = 0, extraCells = null, colSpan = 5 }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true); // on by default
  const [view, setView] = useState('table');
  const [attemptIdx, setAttemptIdx] = useState(null); // null = defaultAttemptIndex
  const [search, setSearch] = useState('');
  const [failedOnly, setFailedOnly] = useState(false);
  const [opReason, setOpReason] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const pollRef = useRef(null);
  const opReasonLoaded = useRef(false);

  const running = RUNNING_PHASES.includes(instance.phase);

  const fetchEvents = useCallback(async (showSpinner) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await listInstanceEvents(stackSetId, instance.accountId, instance.region, running ? 'live' : 'auto');
      setEvents(res?.data || []);
      setNow(Date.now());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [stackSetId, instance.accountId, instance.region, running]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && events === null) fetchEvents(true);
  };

  // Auto-refresh while expanded and the toggle is on (default on). A background
  // refresh (showSpinner=false) never blanks the table.
  useEffect(() => {
    if (!open || !autoRefresh) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(() => fetchEvents(false), running ? EVENTS_POLL_RUNNING_MS : EVENTS_POLL_IDLE_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [open, autoRefresh, fetchEvents, running]);

  // Issue 3: with no events, find this cell's reason in the recent operations'
  // persisted results (covers cells left out because the role was not usable).
  useEffect(() => {
    if (!open || running || !events || events.length > 0 || opReasonLoaded.current) return undefined;
    opReasonLoaded.current = true;
    let cancelled = false;
    let finished = false;
    (async () => {
      try {
        const ops = (await listOperations(stackSetId))?.data || [];
        for (const op of ops.slice(0, OP_RESULT_LOOKBACK)) {
          // eslint-disable-next-line no-await-in-loop
          const rows = (await getOperationResults(stackSetId, op.id))?.data || [];
          const hit = rows.find((r) => r.accountId === instance.accountId && r.region === instance.region);
          if (hit) {
            if (!cancelled && hit.statusReason) setOpReason(hit.statusReason);
            break;
          }
        }
      } catch {
        // Best effort: the instance's own reason is still shown.
      }
      finished = true;
    })();
    // A refetch that lands mid-lookup re-runs this effect; only an interrupted
    // lookup is re-armed, so a finished one is not repeated on every poll.
    return () => { cancelled = true; if (!finished) opReasonLoaded.current = false; };
  }, [open, running, events, stackSetId, instance.accountId, instance.region]);

  const attempts = useMemo(() => buildAttempts(events || [], undefined, now), [events, now]);
  const selectedIdx = attemptIdx === null || attemptIdx >= attempts.length ? defaultAttemptIndex(attempts) : attemptIdx;
  const attempt = attempts[selectedIdx] || null;

  const tableEvents = useMemo(() => {
    const base = attempt ? attempt.events : (events || []);
    const q = search.trim().toLowerCase();
    return base.filter((ev) => {
      if (failedOnly && !/FAILED/.test(ev.resourceStatus || '')) return false;
      if (!q) return true;
      return [ev.logicalResourceId, ev.resourceType, ev.resourceStatusReason]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [attempt, events, search, failedOnly]);

  const reasons = [instance.statusReason, opReason].filter(Boolean)
    .filter((r, i, all) => all.indexOf(r) === i);
  // The timeline draws a failed create together with its automatic cleanup.
  const timelineAttempt = useMemo(() => withCleanup(attempts, selectedIdx), [attempts, selectedIdx]);
  const roleHint = reasons.some(isRoleTrustReason);
  const noEvents = events && events.length === 0 && !loading;

  return (
    <>
      {/* The summary row draws the group's separator via bodyCellSx, exactly
          like every other portal table; the collapse row below adds its own
          only while it is open. */}
      <TableRow hover sx={rowSx(index)}>
        <TableCell sx={{ ...bodyCellSx, width: 40 }}>
          <IconButton size="small" onClick={toggle} aria-label="stack events">
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={bodyCellSx}>{instance.accountId}</TableCell>
        <TableCell sx={bodyCellSx}>{instance.region}</TableCell>
        <TableCell sx={bodyCellSx}>
          {isCancelledCell(instance) ? (
            <Tooltip title="Not attempted: the operation stopped after its failure tolerance was exceeded. Retry includes it.">
              <Chip size="small" label="Cancelled" sx={statusChipSx('neutral')} />
            </Tooltip>
          ) : (
            <Chip size="small" label={formatStatusLabel(instance.phase)} sx={statusChipSx(intentFromColor(phaseColor(instance.phase)))} />
          )}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, maxWidth: 380, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          <ReasonText reason={instance.statusReason} />
        </TableCell>
        {extraCells}
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? `1px solid ${CK.border}` : 'none' }} colSpan={colSpan}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ ...insetPanelSx, p: 2, my: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem' }}>Stack events</Typography>
                <Tooltip title="Refresh now">
                  <span>
                    <IconButton size="small" onClick={() => fetchEvents(true)} disabled={loading} aria-label="refresh stack events">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <FormControlLabel
                  sx={{ ml: 0.5, mr: 0 }}
                  control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
                  label={(
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
                      Auto-refresh
                    </Typography>
                  )}
                />
                {autoRefresh && <Chip size="small" label="Live" sx={statusChipSx('info')} />}
                {loading && <CircularProgress size={14} thickness={5} sx={{ color: CK.blue }} />}
              </Box>
              {error && <Alert severity="error" sx={{ mb: 1, borderRadius: '8px' }}>{error}</Alert>}
              {noEvents && (running || reasons.length === 0) && (
                <Typography sx={{ ...emptyTextSx, fontSize: '0.875rem' }}>
                  No stack events yet — the stack may not exist yet, or it was deleted.
                </Typography>
              )}
              {noEvents && !running && reasons.length > 0 && (
                <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    No stack was created in this account. Reason: {reasons.map((r) => parseFailureReason(r).summary).join(' — ')}
                  </Typography>
                  {roleHint && (
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem', mt: 0.5 }}>
                      The execution role was not usable yet. Retry, or check the role on the Execution roles page.
                    </Typography>
                  )}
                </Alert>
              )}
              {events && events.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={view}
                      onChange={(_, v) => { if (v) setView(v); }}
                      aria-label="events view"
                    >
                      <ToggleButton value="table" sx={{ textTransform: 'none', fontFamily: FONT_BODY, py: 0.25 }}>Table</ToggleButton>
                      <ToggleButton value="timeline" sx={{ textTransform: 'none', fontFamily: FONT_BODY, py: 0.25 }}>Timeline</ToggleButton>
                    </ToggleButtonGroup>
                    {attempts.length > 0 && (
                      <TextField
                        select
                        size="small"
                        label="Attempt"
                        value={selectedIdx}
                        onChange={(e) => setAttemptIdx(Number(e.target.value))}
                        sx={{ minWidth: 240 }}
                      >
                        {attempts.map((a, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <MenuItem key={i} value={i}>
                            {`#${i + 1} ${formatStatusLabel(a.kind)} · ${new Date(a.startedAt).toLocaleString()} · ${formatStatusLabel(a.status)}${isCleanupAttempt(attempts, i) ? ' (cleanup)' : ''}${i === attempts.length - 1 ? ' (latest)' : ''}`}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    {attempt && (
                      <Chip size="small" label={formatStatusLabel(attempt.finalStatus || attempt.status)} sx={statusChipSx(intentFromColor(attemptResultColor(attempt.status)))} />
                    )}
                    {view === 'timeline' && timelineAttempt && timelineAttempt.cleanupStatus !== undefined && (
                      <Chip
                        size="small"
                        label={`Cleanup: ${formatStatusLabel(timelineAttempt.cleanupFinalStatus || timelineAttempt.cleanupStatus)}`}
                        sx={statusChipSx(intentFromColor(attemptResultColor(timelineAttempt.cleanupStatus)))}
                      />
                    )}
                    {view === 'table' && (
                      <>
                        <TextField
                          size="small"
                          placeholder="Search logical ID, type or reason"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          sx={{ flex: '1 1 220px', maxWidth: 320 }}
                        />
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={<Switch size="small" checked={failedOnly} onChange={(e) => setFailedOnly(e.target.checked)} />}
                          label={<Typography sx={smallLabelSx}>Failed only</Typography>}
                        />
                      </>
                    )}
                  </Box>
                  {view === 'timeline' && <StackTimeline attempt={timelineAttempt} />}
                  {view === 'table' && tableEvents.length === 0 && (
                    <Typography sx={{ ...emptyTextSx, fontSize: '0.875rem' }}>No events match the filters.</Typography>
                  )}
                  {view === 'table' && tableEvents.length > 0 && (
                    <TableContainer sx={{ maxHeight: 360 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={tableHeadRowSx}>
                            <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Timestamp</TableCell>
                            <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Logical ID</TableCell>
                            <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Physical ID</TableCell>
                            <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Type</TableCell>
                            <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Status</TableCell>
                            <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tableEvents.map((ev, i) => (
                            <TableRow key={ev.eventId || `${ev.timestamp}-${ev.logicalResourceId}-${i}`} hover sx={rowSx(i)}>
                              <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                                {new Date(ev.timestamp).toLocaleString()}
                              </TableCell>
                              <TableCell sx={bodyCellSx}>{ev.logicalResourceId}</TableCell>
                              <TableCell sx={{ ...bodyCellSx, fontFamily: FONT_MONO, fontSize: '0.75rem', maxWidth: 260, wordBreak: 'break-all' }}>
                                {ev.physicalResourceId}
                              </TableCell>
                              <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{ev.resourceType}</TableCell>
                              <TableCell sx={bodyCellSx}>
                                <Chip size="small" label={formatStatusLabel(ev.resourceStatus)} sx={statusChipSx(intentFromColor(eventColor(ev.resourceStatus)))} />
                              </TableCell>
                              <TableCell sx={{ ...bodyCellSx, maxWidth: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {ev.resourceStatusReason}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
